import { forwardRef, memo, useCallback, useRef } from 'react';
import { AlertTriangleIcon, XIcon } from '@page/icons';
import { tv, type VariantProps } from 'tailwind-variants';

import { useMergedRefs } from '@page/core/hooks/common';
import { cn } from '../../lib/styles';
import { Label } from '../Label';
import { IconButton } from '../IconButton';
import { Tooltip } from '../Tooltip';
import { fieldStyles } from '../Field';

export const inputStyles = tv({
  extend: fieldStyles,
  variants: {
    showFocusOutline: {
      true: 'outline-1 outline-bg-inter-secondary-default focus-visible:outline-border-input-select',
      false: 'outline-none',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'sm',
  },
});

export type InputProps = Omit<React.ComponentProps<'input'>, 'size' | 'ref'> &
  VariantProps<typeof inputStyles> & {
    type?: string;
    label?: React.ReactNode;
    error?: React.ReactNode;
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
    // TODO: make all consumers explicitly set clearable to true/false instead of relying on implicit behavior
    /**
     * If true, shows a clear (X) button when there is a value in the input.
     * Default is true if the label or placeholder includes the word "search".
     */
    clearable?: boolean;
    forceShowClearButton?: boolean;
    onClear?: () => void;
    containerClassName?: string;
    inputClassName?: string;
    leftIconClassName?: string;
    rightIconClassName?: string;
    labelClassName?: string;
    onLabelClick?: (e: React.MouseEvent<HTMLLabelElement>) => void;
    /**
     * Labeled inputs keep a one-line slot under the field for an error, so the layout holds still when
     * one appears. Pass false where that slot is dead space (compact forms, menu panels): the message
     * then renders only while there is one.
     */
    reserveErrorSpace?: boolean;
    component?: React.ComponentType;
    /**
     * Input mask pattern. Only works when an MaskInput component (e.g. from react-imask)
     * is passed as the `component` prop.
     */
    mask?: string;
  };

const InputBase = (
  {
    id,
    value,
    onChange,
    className,
    type,
    variant,
    size,
    label,
    error,
    leftIcon,
    rightIcon,
    containerClassName,
    inputClassName,
    leftIconClassName,
    rightIconClassName,
    clearable: clearableProp,
    forceShowClearButton,
    onClear,
    labelClassName,
    onLabelClick,
    reserveErrorSpace = true,
    component,
    showFocusOutline = true,
    ...rest
  }: InputProps,
  forwardedRef: React.ForwardedRef<HTMLInputElement>,
) => {
  const actualId = id || rest.name;
  const clearable =
    clearableProp ??
    Boolean(label?.toString().toLowerCase().includes('search') || rest.placeholder?.toLowerCase().includes('search'));
  const internalRef = useRef<HTMLInputElement | null>(null);
  const ref = useMergedRefs(forwardedRef, internalRef);

  const handleClear = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      if (internalRef.current) {
        internalRef.current.value = '';
        internalRef.current.focus();
      }
      const changeEvent = {
        ...new Event('input', { bubbles: true }),
        target: internalRef.current,
      } as unknown as React.ChangeEvent<HTMLInputElement>;
      onChange?.(changeEvent);
      onClear?.();
    },
    [onChange, onClear],
  );

  const clearIcon =
    clearable && (value || forceShowClearButton) ? (
      <IconButton
        size="sm"
        onClick={handleClear}
        variant="tertiary"
        aria-label="Clear"
        className="hover:bg-transparent"
      >
        <XIcon className="size-5" />
      </IconButton>
    ) : null;

  const rightIconsCount = [rightIcon, clearIcon].filter(Boolean).length;

  const Component = component || 'input';

  const renderRightIcon = () => {
    const icon =
      !label && error ? (
        <Tooltip label={error} disabled={!error} withinPortal className="z-1000" open>
          <AlertTriangleIcon className="size-5 fill-icon-error cursor-pointer" />
        </Tooltip>
      ) : (
        rightIcon
      );

    return <div className={cn('absolute flex items-center right-3', rightIconClassName)}>{icon}</div>;
  };

  const inputElement = (
    <div className={cn('relative flex items-center group', containerClassName)}>
      {leftIcon && <div className={cn('absolute left-3 flex items-center', leftIconClassName)}>{leftIcon}</div>}

      <Component
        id={actualId}
        ref={ref}
        value={value}
        onChange={onChange}
        type={type}
        data-slot="input"
        className={cn(
          'placeholder:text-text-tertiary placeholder:font-normal disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 w-full peer',
          inputStyles({ variant, size, showFocusOutline }),
          {
            'outline-text-destructive!': error,
            'pl-9': leftIcon,
            'pr-8': rightIconsCount === 1,
            'pr-14': rightIconsCount === 2,
          },
          label ? inputClassName : inputClassName || className,
        )}
        {...rest}
      />

      {!!clearIcon && (
        <IconButton
          size="sm"
          onClick={handleClear}
          variant="tertiary"
          aria-label="Clear"
          className={cn('absolute', rightIcon ? 'right-8' : 'right-1')}
        >
          <XIcon className="size-4 min-w-4" />
        </IconButton>
      )}

      {renderRightIcon()}
    </div>
  );

  if (!label) return inputElement;

  return (
    <div className={cn('flex flex-col', className)}>
      <Label htmlFor={actualId} className={cn('mb-2', labelClassName)} onClick={onLabelClick}>
        {label}
      </Label>

      {inputElement}

      {reserveErrorSpace || error ? (
        <div className="h-5 pt-1 text-text-destructive font-body-sm text-left">{error}</div>
      ) : null}
    </div>
  );
};

export const Input = memo(forwardRef(InputBase));
